// src/hooks/useFilingGenerator.js
import { useState } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const useFilingGenerator = (planTracker) => {
  const [isProcessing,       setIsProcessing      ] = useState(false);
  const [processingError,    setProcessingError   ] = useState('');
  const [filingData,         setFilingData        ] = useState(null);
  const [transactions,       setTransactions      ] = useState([]);
  const [faxStatus,          setFaxStatus         ] = useState(null);
  const [faxConfirmation,    setFaxConfirmation   ] = useState(null);
  const [generatedPdfBase64, setGeneratedPdfBase64] = useState(null);

  // ─── STEP 1: Parse bank statement ────────────────────────────────────────
  const parseStatement = async (formData) => {
    setIsProcessing(true);
    setProcessingError('');
    setTransactions([]);
    setFilingData(null);
    setGeneratedPdfBase64(null);
    setFaxStatus(null);
    setFaxConfirmation(null);

    if (planTracker) {
      const canGenerate = planTracker.canGenerateQR();
      if (!canGenerate) {
        const error = planTracker.getLimitReason() || 'You have reached your limit. Please upgrade.';
        setProcessingError(error);
        setIsProcessing(false);
        throw new Error(error);
      }
    }

    try {
      const response = await fetch(`${API}/api/parse`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          statementText: formData.statementText,
          llcName:       formData.llcName,
          taxYear:       formData.taxYear,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to parse bank statement');
      }

      const { transactions: parsed, diagnosis } = await response.json();

      if (!parsed || parsed.length === 0) {
        throw new Error(
          diagnosis ||
          'No transactions found between you and your LLC. ' +
          'Check that you uploaded the correct statement.'
        );
      }

      if (planTracker) {
        const consumed = await planTracker.consumeCredit();
        if (!consumed) throw new Error('Failed to track usage. Please try again.');
      }

      setTransactions(parsed);
      setFilingData(formData);

      return { transactions: parsed, formData };

    } catch (error) {
      setProcessingError(error.message || 'Failed to analyze statement');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  // ─── STEP 2: Generate filled PDF ─────────────────────────────────────────
  const generatePDF = async (confirmedTransactions, overrideFormData) => {
    const data = overrideFormData || filingData;
    if (!data) throw new Error('No filing data. Please start over.');

    setIsProcessing(true);
    setProcessingError('');

    try {
      const response = await fetch(`${API}/api/generate`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          llcName:          data.llcName,
          ein:              data.ein,
          taxYear:          data.taxYear,
          ownerName:        data.ownerName,
          ownerCountry:     data.ownerCountry,
          ownerTitle:       data.ownerTitle       || 'Foreign Owner / Member',
          ownerAddress:     data.ownerAddress     || '',
          city:             data.city             || '',
          stateCode:        data.stateCode        || '',
          country:          data.country          || data.ownerCountry || '',
          businessActivity: data.businessActivity || '',
          businessCode:     data.businessCode     || '',
          isFirstYear:      data.isFirstYear      || false,
          signatureBase64:  data.signatureBase64  || null,
          transactions:     confirmedTransactions,
          postalCode:       data.postalCode || '',
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to generate Form 5472');
      }

      const { pdfBase64 } = await response.json();
      setGeneratedPdfBase64(pdfBase64);
      return pdfBase64;

    } catch (error) {
      setProcessingError(error.message || 'Failed to generate PDF');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  // ─── STEP 3: Fax to IRS ──────────────────────────────────────────────────
  // FIX: Two-phase status:
  //   1. 'faxed'     — faxId received from queue, shown in sidebar immediately
  //   2. 'confirmed' — polling confirmed delivery
  //   3. 'failed'    — polling or send error
  const sendFax = async (pdfBase64) => {
    if (!pdfBase64) throw new Error('No PDF generated. Please generate first.');

    // Phase 1: show sending spinner
    setFaxStatus('sending');
    setProcessingError('');

    try {
      const response = await fetch(`${API}/api/fax`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pdfBase64,
          llcName:       filingData.llcName,
          taxYear:       filingData.taxYear,
          customerEmail: filingData.ownerEmail || '',
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to send fax');
      }

      const result = await response.json();

      // Phase 2: faxId received — immediately mark as 'faxed' so sidebar shows Faxed label
      // The confirmation object at this stage has faxId but status is still in_progress.
      if (result.faxId || result.confirmation?.faxId) {
        const faxId = result.faxId || result.confirmation?.faxId;
        setFaxStatus('faxed');
        setFaxConfirmation({
          faxId,
          sentTo:   result.confirmation?.sentTo || result.sentTo || 'IRS Fax Line',
          pages:    result.confirmation?.pages  || result.pages  || 4,
          status:   'Faxed — awaiting delivery confirmation',
          queuedAt: new Date().toISOString(),
        });
        return { faxId, status: 'faxed' };
      }

      // If backend already polled to completion and returned a full confirmation:
      if (result.confirmation) {
        setFaxStatus('confirmed');
        setFaxConfirmation(result.confirmation);
        return result.confirmation;
      }

      // Fallback — shouldn't happen but handle gracefully
      setFaxStatus('faxed');
      return result;

    } catch (error) {
      setFaxStatus('failed');
      setProcessingError(error.message || 'Failed to send fax to IRS');
      throw error;
    }
  };

  // ─── GENERIC: single-shot submit for EIN / ITIN ──────────────────────────
  // Same credit-check + consume-credit + error handling as parseStatement,
  // just POSTs `payload` straight to `endpoint` and stores whatever comes
  // back into the same state the rest of the hook already uses:
  //   - data.confirmation                → faxConfirmation
  //   - data.pdfBase64 / data.w7Base64   → generatedPdfBase64 (primary PDF)
  //   - data.coverBase64 (ITIN's 2nd pdf)→ tucked onto faxConfirmation.coverBase64
  // `meta` (optional) tells the hook how to record this submission in
  // filing_history so it shows up in the sidebar:
  //   - meta.type       → 'ein' | 'itin' (defaults to no history write if omitted).
  //     Annual Report used to go through here too, but it's now fully
  //     client-side (see components/AnnualReport.js) and never calls this.
  //   - meta.status     → FILING_STATUS value to store (defaults to FILED)
  //   - meta.buildTitle → (payload, data) => string, used as the sidebar title
  const submitTool = async (endpoint, payload, meta = {}) => {
    setIsProcessing(true);
    setProcessingError('');
    setFaxConfirmation(null);
    setGeneratedPdfBase64(null);

    if (planTracker) {
      const canGenerate = planTracker.canGenerateQR();
      if (!canGenerate) {
        const error = planTracker.getLimitReason() || 'You have reached your limit. Please upgrade.';
        setProcessingError(error);
        setIsProcessing(false);
        throw new Error(error);
      }
    }

    try {
      const response = await fetch(`${API}${endpoint}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Request failed');

      if (planTracker) {
        const consumed = await planTracker.consumeCredit();
        if (!consumed) throw new Error('Failed to track usage. Please try again.');
      }

      const primaryPdf = data.pdfBase64 || data.w7Base64 || null;
      const confirmation = data.confirmation
        ? { ...data.confirmation, coverBase64: data.coverBase64 || null }
        : null;

      setFilingData(payload);
      setGeneratedPdfBase64(primaryPdf);
      setFaxConfirmation(confirmation);

      // Record this filing in history so it shows up in the sidebar.
      // Without meta.type (e.g. an unlisted future tool), skip silently
      // rather than writing a mislabeled record.
      if (planTracker?.recordToolFiling && meta.type) {
        try {
          const title = meta.buildTitle
            ? meta.buildTitle(payload, data)
            : meta.title;
          await planTracker.recordToolFiling({
            type:         meta.type,
            title,
            formData:     payload,
            pdfBase64:    primaryPdf,
            confirmation,
            status:       meta.status,
          });
        } catch (historyError) {
          // Don't fail the whole submission just because the sidebar
          // record couldn't be written — the filing itself already succeeded.
          console.error('[submitTool] failed to record filing history:', historyError);
        }
      }

      return data;

    } catch (error) {
      setProcessingError(error.message || 'Something went wrong. Please try again.');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  // ─── GENERIC: two-phase flow for tools with a review/sign step (EIN) ─────
  // Unlike submitTool (one-shot: generate + send + record history in a
  // single call), some tools need a preview step in between — build a PDF,
  // let the user review/sign it, THEN send it. Split into two functions:
  //
  //   generateToolPDF — builds the PDF only, no credit consumed by default.
  //     Pass { checkCredit: true } for the FIRST call (the point where the
  //     user commits to the filing) — same gate as parseStatement/submitTool.
  //     Subsequent calls (e.g. regenerating with a freshly-applied signature
  //     right before sending) should NOT re-check/re-consume credit, same
  //     as how generatePDF never touches credit once parseStatement already
  //     has for the 5472 flow.
  //
  //   sendToolPDF — posts the (already generated, already signed) pdfBase64
  //     to the send endpoint. No credit logic here — that already happened
  //     in generateToolPDF. Records filing_history on success via `meta`,
  //     same shape as submitTool's meta.
  const generateToolPDF = async (endpoint, payload, opts = {}) => {
    const { checkCredit = false } = opts;
    setIsProcessing(true);
    setProcessingError('');

    if (checkCredit && planTracker) {
      const canGenerate = planTracker.canGenerateQR();
      if (!canGenerate) {
        const error = planTracker.getLimitReason() || 'You have reached your limit. Please upgrade.';
        setProcessingError(error);
        setIsProcessing(false);
        throw new Error(error);
      }
    }

    try {
      const response = await fetch(`${API}${endpoint}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to generate PDF');

      if (checkCredit && planTracker) {
        const consumed = await planTracker.consumeCredit();
        if (!consumed) throw new Error('Failed to track usage. Please try again.');
      }

      setFilingData(payload);
      setGeneratedPdfBase64(data.pdfBase64 || null);
      return data;

    } catch (error) {
      setProcessingError(error.message || 'Failed to generate PDF');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const sendToolPDF = async (endpoint, payload, meta = {}) => {
    setIsProcessing(true);
    setProcessingError('');

    try {
      const response = await fetch(`${API}${endpoint}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send');

      const confirmation = data.confirmation || null;
      setFaxConfirmation(confirmation);

      if (planTracker?.recordToolFiling && meta.type) {
        try {
          const title = meta.buildTitle
            ? meta.buildTitle(payload, data)
            : meta.title;
          await planTracker.recordToolFiling({
            type:         meta.type,
            title,
            formData:     meta.formData || payload,
            pdfBase64:    meta.pdfBase64 ?? payload.pdfBase64 ?? generatedPdfBase64 ?? null,
            confirmation,
            status:       meta.status,
          });
        } catch (historyError) {
          console.error('[sendToolPDF] failed to record filing history:', historyError);
        }
      }

      return data;

    } catch (error) {
      setProcessingError(error.message || 'Failed to send. Please try again.');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  // ─── Resume tracking for a session reopened from history ─────────────────
  // Called when the user loads a filing from the sidebar that's still 'faxed'
  // (faxId received, delivery not yet confirmed). Re-arms faxStatus/faxConfirmation
  // so the polling effect in FilingGenerator.js picks it back up and checks
  // FaxDrop again immediately, instead of showing stale "Faxed" state forever
  // just because the original tab was closed before delivery resolved.
  const resumeFaxTracking = (faxId, priorConfirmation) => {
    if (!faxId) return;
    setFaxStatus('faxed');
    setFaxConfirmation({
      faxId,
      sentTo:   priorConfirmation?.sentTo   || 'IRS Fax Line',
      pages:    priorConfirmation?.pages    || 4,
      status:   'Faxed — awaiting delivery confirmation',
      queuedAt: priorConfirmation?.queuedAt || priorConfirmation?.confirmedAt || null,
    });
  };

  // ─── Check delivery status (single call — used by the polling effect) ────
  const checkFaxDelivery = async (faxId) => {
    if (!faxId) return null;
    try {
      const response = await fetch(`${API}/api/fax-status/${faxId}`);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to check fax status');
      }
      return await response.json();
    } catch (error) {
      console.error('[checkFaxDelivery] error:', error);
      return null;
    }
  };

  // ─── Apply a status-check result to local state ──────────────────────────
  const applyDeliveryUpdate = (statusData) => {
    if (!statusData) return;

    if (statusData.status === 'delivered') {
      setFaxStatus('confirmed');
      setFaxConfirmation(prev => ({
        ...prev,
        faxId:       statusData.faxId,
        status:      'Delivered',
        delivered:   true,
        confirmedAt: statusData.deliveredAt || new Date().toISOString(),
        pages:       statusData.pages || prev?.pages,
      }));
    } else if (statusData.status === 'failed') {
      setFaxStatus('failed');
      setFaxConfirmation(prev => ({
        ...prev,
        faxId:         statusData.faxId,
        status:        'Failed',
        failureReason: statusData.failureReason || 'The fax could not be delivered.',
      }));
    }
    // status === 'in_progress' → no change, still shown as 'faxed'
  };

  // ─── Update fax to delivered (call this when backend polling completes) ──
  const markFaxDelivered = (deliveryConfirmation) => {
    setFaxStatus('confirmed');
    setFaxConfirmation(prev => ({
      ...prev,
      ...deliveryConfirmation,
      status: 'Delivered',
    }));
  };

  // ─── Download PDF locally ─────────────────────────────────────────────────
  // Pass base64Override/filenameOverride to download something other than
  // the primary generatedPdfBase64 (e.g. ITIN's cover letter).
  const downloadPDF = (base64Override, filenameOverride) => {
    const base64 = base64Override || generatedPdfBase64;
    if (!base64) return;
    const filename = filenameOverride
      || `Form-5472-${filingData?.llcName || 'LLC'}-${filingData?.taxYear || ''}.pdf`;
    const blob = b64toBlob(base64, 'application/pdf');
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    setFilingData(null);
    setTransactions([]);
    setGeneratedPdfBase64(null);
    setFaxStatus(null);
    setFaxConfirmation(null);
    setProcessingError('');
  };

  return {
    isProcessing,
    processingError,
    filingData,
    setFilingData,
    transactions,
    setTransactions,
    generatedPdfBase64,
    setGeneratedPdfBase64,
    faxStatus,
    faxConfirmation,
    parseStatement,
    generatePDF,
    sendFax,
    submitTool,
    generateToolPDF,
    sendToolPDF,
    resumeFaxTracking,
    markFaxDelivered,
    checkFaxDelivery,
    applyDeliveryUpdate,
    downloadPDF,
    clearAll,
  };
};

function b64toBlob(b64Data, contentType = '', sliceSize = 512) {
  const byteChars  = atob(b64Data);
  const byteArrays = [];
  for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
    const slice    = byteChars.slice(offset, offset + sliceSize);
    const byteNums = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) byteNums[i] = slice.charCodeAt(i);
    byteArrays.push(new Uint8Array(byteNums));
  }
  return new Blob(byteArrays, { type: contentType });
}

export default useFilingGenerator;